/**
 *
 * @author    Jerry Bendy
 * @since     4/12/2017
 */

function touchX(event) {
  return event.touches[0].clientX;
}

function touchY(event) {
  return event.touches[0].clientY;
}

const isPassiveSupported = (function () {
  let supportsPassive = false;
  try {
    const opts = Object.defineProperty({}, "passive", {
      get() {
        return (supportsPassive = true);
      },
    });
    window.addEventListener("test", null, opts);
  } catch (e) {}
  return supportsPassive;
})();

const vueTouchEvents = {
  install(Vue, options) {
    // Set default options
    options = Object.assign(
      {},
      {
        disableClick: false,
        tapTolerance: 10,
        swipeMaxTolerance: 100,
        swipeMinTolerance: 30,
        longTapTimeInterval: 400,
        touchClass: "",
      },
      options || {}
    );

    function touchStartEvent(event) {
      const $this = this.$$touchObj;

      $this.supportTouch = true;

      if ($this.touchStarted) {
        return;
      }

      addTouchClass(this);

      $this.touchStarted = true;

      $this.touchMoved = false;
      $this.swipeOutBounded = false;

      $this.startX = touchX(event);
      $this.startY = touchY(event);

      $this.currentX = 0;
      $this.currentY = 0;

      $this.touchStartTime = event.timeStamp;

      triggerEvent(event, this, "start");
    }

    function touchMoveEvent(event) {
      const $this = this.$$touchObj;

      $this.currentX = touchX(event);
      $this.currentY = touchY(event);

      if (!$this.touchMoved) {
        const tapTolerance = options.tapTolerance;

        $this.touchMoved =
          Math.abs($this.startX - $this.currentX) > tapTolerance ||
          Math.abs($this.startY - $this.currentY) > tapTolerance;

        if ($this.touchMoved) {
          triggerEvent(event, this, "moved");
        }
      } else if (!$this.swipeOutBounded) {
        const swipeOutBounded = options.swipeMaxTolerance;

        $this.swipeOutBounded =
          Math.abs($this.startX - $this.currentX) > swipeOutBounded &&
          Math.abs($this.startY - $this.currentY) > swipeOutBounded;
      }

      if ($this.touchMoved) {
        triggerEvent(event, this, "moving");
      }
    }

    function touchCancelEvent() {
      const $this = this.$$touchObj;

      removeTouchClass(this);

      $this.mouseDown = false;
      $this.touchStarted = $this.touchMoved = false;
      $this.startX = $this.startY = 0;
    }

    function touchEndEvent(event) {
      const $this = this.$$touchObj;

      $this.touchStarted = false;

      removeTouchClass(this);

      if (!$this.touchMoved) {
        // detect if this is a longTap event or not
        if (
          $this.callbacks.longtap &&
          event.timeStamp - $this.touchStartTime > options.longTapTimeInterval
        ) {
          event.preventDefault();
          triggerEvent(event, this, "longtap");
        } else {
          // emit tap event
          triggerEvent(event, this, "tap");
        }
        triggerEvent(event, this, "end");
      } else if (!$this.swipeOutBounded) {
        const swipeInBounded = options.swipeMinTolerance;
        const deltaX = Math.abs($this.startX - $this.currentX);
        const deltaY = Math.abs($this.startY - $this.currentY);
        let direction = "";

        if (deltaX < swipeInBounded && deltaY < swipeInBounded) return;

        if (deltaY > deltaX) {
          direction = $this.startY > $this.currentY ? "top" : "bottom";
        } else {
          direction = $this.startX > $this.currentX ? "left" : "right";
        }

        // Only emit the specified event when it has modifiers
        if ($this.callbacks[`swipe.${direction}`]) {
          triggerEvent(event, this, `swipe.${direction}`, direction);
        } else {
          // Emit a common event when it has no any modifier
          triggerEvent(event, this, "swipe", direction);
        }
      }
    }

    function clickEvent(event) {
      const $this = this.$$touchObj;

      if (!$this.supportTouch && !options.disableClick) {
        triggerEvent(event, this, "tap");
      }
    }

    function mouseDownEvent(event) {
      const $this = this.$$touchObj;

      $this.mouseDown = true;

      if (!$this.supportTouch && !options.disableClick) {
        triggerEvent(event, this, "start");
      }
    }

    function mouseUpEvent(event) {
      const $this = this.$$touchObj;

      $this.mouseDown = false;

      if (!$this.supportTouch && !options.disableClick) {
        triggerEvent(event, this, "end");
      }
    }

    function mouseEnterEvent() {
      addTouchClass(this);
    }

    function mouseMoveEvent(event) {
      const $this = this.$$touchObj;

      if ($this.mouseDown && !$this.supportTouch && !options.disableClick) {
        triggerEvent(event, this, "moving");
      }
    }

    function mouseLeaveEvent() {
      const $this = this.$$touchObj;

      removeTouchClass(this);

      $this.mouseDown = false;
    }

    function triggerEvent(e, $el, eventType, param) {
      const $this = $el.$$touchObj;

      // get the callback list
      const callbacks = $this.callbacks[eventType] || [];
      if (callbacks.length === 0) {
        return null;
      }

      for (let i = 0; i < callbacks.length; i++) {
        const binding = callbacks[i];

        if (binding.modifiers.stop) {
          e.stopPropagation();
        }

        if (binding.modifiers.prevent) {
          e.preventDefault();
        }

        // handle `self` modifier`
        if (binding.modifiers.self && e.target !== e.currentTarget) {
          continue;
        }

        if (typeof binding.value === "function") {
          if (param) {
            binding.value(param, e);
          } else {
            binding.value(e);
          }
        }
      }
    }

    function addTouchClass($el) {
      const className = $el.$$touchClass || options.touchClass;
      className && $el.classList.add(className);
    }

    function removeTouchClass($el) {
      const className = $el.$$touchClass || options.touchClass;
      className && $el.classList.remove(className);
    }

    Vue.directive("touch", {
      beforeMount: function ($el, binding) {
        $el.$$touchObj = $el.$$touchObj || {
          // will change to true when `touchstart` event first trigger
          supportTouch: false,
          // an object contains all callbacks registered,
          // key is event name, value is an array
          callbacks: {},
          // prevent bind twice, set to true when event bound
          hasBindTouchEvents: false,
        };

        // register callback
        const eventType = binding.arg || "tap";
        switch (eventType) {
          case "swipe":
            var _m = binding.modifiers;
            if (_m.left || _m.right || _m.top || _m.bottom) {
              for (const i in binding.modifiers) {
                if (["left", "right", "top", "bottom"].indexOf(i) >= 0) {
                  const _e = `swipe.${i}`;
                  $el.$$touchObj.callbacks[_e] =
                    $el.$$touchObj.callbacks[_e] || [];
                  $el.$$touchObj.callbacks[_e].push(binding);
                }
              }
            } else {
              $el.$$touchObj.callbacks.swipe =
                $el.$$touchObj.callbacks.swipe || [];
              $el.$$touchObj.callbacks.swipe.push(binding);
            }
            break;

          default:
            $el.$$touchObj.callbacks[eventType] =
              $el.$$touchObj.callbacks[eventType] || [];
            $el.$$touchObj.callbacks[eventType].push(binding);
        }

        // prevent bind twice
        if ($el.$$touchObj.hasBindTouchEvents) {
          return;
        }

        const passiveOpt = isPassiveSupported ? { passive: true } : false;
        $el.addEventListener("touchstart", touchStartEvent, passiveOpt);
        $el.addEventListener("touchmove", touchMoveEvent, passiveOpt);
        $el.addEventListener("touchcancel", touchCancelEvent);
        $el.addEventListener("touchend", touchEndEvent);

        if (!options.disableClick) {
          $el.addEventListener("click", clickEvent);
          $el.addEventListener("mousedown", mouseDownEvent);
          $el.addEventListener("mouseup", mouseUpEvent);
          $el.addEventListener("mouseenter", mouseEnterEvent);
          $el.addEventListener("mousemove", mouseMoveEvent);
          $el.addEventListener("mouseleave", mouseLeaveEvent);
        }

        // set bind mark to true
        $el.$$touchObj.hasBindTouchEvents = true;
      },

      unmounted($el) {
        $el.removeEventListener("touchstart", touchStartEvent);
        $el.removeEventListener("touchmove", touchMoveEvent);
        $el.removeEventListener("touchcancel", touchCancelEvent);
        $el.removeEventListener("touchend", touchEndEvent);

        if (!options.disableClick) {
          $el.removeEventListener("click", clickEvent);
          $el.removeEventListener("mousedown", mouseDownEvent);
          $el.removeEventListener("mouseup", mouseUpEvent);
          $el.removeEventListener("mousemove", mouseMoveEvent);
          $el.removeEventListener("mouseenter", mouseEnterEvent);
          $el.removeEventListener("mouseleave", mouseLeaveEvent);
        }

        // remove vars
        delete $el.$$touchObj;
      },
    });

    Vue.directive("touch-class", {
      beforeMount: function ($el, binding) {
        $el.$$touchClass = binding.value;
      },
      unmounted($el) {
        delete $el.$$touchClass;
      },
    });
  },
};

/*
 * Exports
 */
if (typeof module === "object") {
  module.exports = vueTouchEvents;
} else if (typeof define === "function" && define.amd) {
  define([], () => vueTouchEvents);
} else if (window.Vue) {
  window.vueTouchEvents = vueTouchEvents;
  Vue.use(vueTouchEvents);
}
